import { CourseFormData } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import {
  Course,
  CoursePayload,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

/** Maps an existing course (server graph) into editable form data. */
export const courseToFormData = (course: Course): CourseFormData => ({
  title: course.title,
  tagline: course.tagline ?? "",
  description: course.description ?? "",
  level: course.level ?? "",
  estimatedHours: course.estimatedHours ?? "",
  targetAudience: course.targetAudience ?? "",
  learningObjectives: course.learningObjectives ?? [],
  courseOverview: course.courseOverview ?? "",
  // Hydrate the existing image uuids — the form posts both on every save, so
  // leaving these null wipes whichever image wasn't re-uploaded this session.
  thumbnailFileUuid: course.thumbnailFileUuid,
  heroBannerFileUuid: course.heroBannerFileUuid,
  tradeIds: course.trades.map((t) => String(t.id)),
  modules: course.modules.map((module) => ({
    id: module.uuid,
    uuid: module.uuid,
    title: module.title,
    description: module.description ?? "",
    difficulty: module.difficulty ?? null,
    estimatedMinutes: module.estimatedMinutes ?? null,
    tags: module.tags ?? [],
    sourceLibraryModuleUuid: null,
    blocks: module.blocks.map((block) => ({
      id: block.uuid,
      uuid: block.uuid,
      type: block.type,
      content: block.content ?? "",
      metadata: block.metadata,
    })),
  })),
});

/** Maps form data into the create/update request payload. */
export const formDataToPayload = (data: CourseFormData): CoursePayload => ({
  title: data.title,
  tagline: data.tagline || null,
  description: data.description || null,
  level: data.level || null,
  estimatedHours: data.estimatedHours || null,
  targetAudience: data.targetAudience || null,
  learningObjectives: data.learningObjectives.filter(
    (objective) => objective.trim() !== "",
  ),
  courseOverview: data.courseOverview || null,
  thumbnailFileUuid: data.thumbnailFileUuid || null,
  heroBannerFileUuid: data.heroBannerFileUuid || null,
  tradeIds: data.tradeIds.map(Number),
  modules: data.modules.map((module, moduleIndex) => ({
    uuid: module.uuid || null,
    title: module.title,
    description: module.description || null,
    difficulty: module.difficulty || null,
    estimatedMinutes: module.estimatedMinutes ?? null,
    tags: module.tags.length > 0 ? module.tags : null,
    orderNumber: moduleIndex,
    sourceLibraryModuleUuid: module.sourceLibraryModuleUuid || null,
    blocks: module.blocks.map((block, blockIndex) => ({
      uuid: block.uuid || null,
      type: block.type,
      content: block.content || null,
      orderNumber: blockIndex,
      metadata: block.metadata ?? null,
    })),
  })),
});

export const emptyCourseFormData = (): CourseFormData => ({
  title: "",
  tagline: "",
  description: "",
  level: "",
  estimatedHours: "",
  targetAudience: "",
  learningObjectives: [],
  courseOverview: "",
  thumbnailFileUuid: null,
  heroBannerFileUuid: null,
  tradeIds: [],
  // Modules are added on the Edit screen only — a new course starts with none.
  modules: [],
});
